import { authenticatedFetch } from "./apiClient";

/** GET /api/admin/overview — system-wide users, plans, usage, revenue and data-vendor headroom. */
export interface AdminOverview {
  as_of: string;
  days: number;
  period: {
    users: { total: number; new_7d: number; new_30d: number; active_7d: number; admins: number };
    subscriptions: Record<string, number>; // "plus:active" -> count
    cancelling: number;
    addons: { live_slot: number; paper_slots: number };
    ai: { turns: number; users: number; credits: number; cost_usd: number; by_bucket: Record<string, number> };
    packs: { sold: number; sold_by_pack: Record<string, number>; credits_outstanding: number };
    backtests: { count: number; users: number };
    launchpad: { running: number; running_live: number; users: number };
  };
  month: {
    ai: { turns: number; credits: number; cost_usd: number; users: number };
    backtests: { count: number };
    packs: { sold: number; sold_by_pack: Record<string, number> };
  };
  revenue: { available: boolean; mrr_usd?: number; subscriptions?: number; by_price?: Record<string, number>; mode?: string };
  system: {
    ai_cost_month_usd: number;
    ai_budget_month_usd: number | null;
    ai_budget_used_pct: number | null;
    twelvedata: { available: boolean; current_usage?: number; plan_limit?: number; plan_category?: string };
  };
}

export interface AdminUserRow {
  clerk_user_id: string;
  email: string | null;
  name: string | null;
  joined_at: string | null;
  last_login_at: string | null;
  is_admin: boolean;
  plan: string;
  subscription: { tier: string | null; status: string | null; cancel_at_period_end: boolean; current_period_end: string | null };
  addons: Record<string, number>;
  ai: {
    turns: number;
    credits_used: number;
    cost_usd: number;
    plan_credits_left: number | null;
    packs_left: number;
    free_prompts_left: number | null;
  };
  packs_bought: number;
  backtests: number;
  deployments_running: number;
  trades: number;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await authenticatedFetch(path, { method: "GET" });
  if (!response.ok) throw new Error(`Admin request failed: ${response.status}`);
  return (await response.json()) as T;
}

export function getAdminOverview(days = 30): Promise<AdminOverview> {
  return getJson<AdminOverview>(`/admin/overview?days=${days}`);
}

export function getAdminUsers(params: { search?: string; limit?: number; offset?: number; days?: number }) {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  q.set("limit", String(params.limit ?? 50));
  q.set("offset", String(params.offset ?? 0));
  q.set("days", String(params.days ?? 30));
  return getJson<{ users: AdminUserRow[]; total: number; limit: number; offset: number; days: number }>(
    `/admin/users?${q.toString()}`
  );
}

/** Public Telegram showcase (admin): your Launchpad deployments and their showcase settings. */
export interface ShowcaseSettings {
  title: string;
  blurb: string | null;
  daily: boolean;
  weekly: boolean;
  enabled: boolean;
}

export interface ShowcaseDeployment {
  deployment_id: string;
  name: string | null;
  symbol: string | null;
  candle_type: string | null;
  mode: string | null;
  status: string | null;
  showcase: ShowcaseSettings | null;
}

export function getShowcase() {
  return getJson<{ deployments: ShowcaseDeployment[]; channel_configured: boolean }>("/admin/showcase");
}

export async function saveShowcase(deploymentId: string, settings: ShowcaseSettings): Promise<void> {
  const r = await authenticatedFetch(`/admin/showcase/${encodeURIComponent(deploymentId)}`, {
    method: "PUT",
    body: JSON.stringify(settings),
  });
  if (!r.ok) throw new Error(`Couldn't save: ${r.status}`);
}

export async function removeShowcase(deploymentId: string): Promise<void> {
  const r = await authenticatedFetch(`/admin/showcase/${encodeURIComponent(deploymentId)}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`Couldn't remove: ${r.status}`);
}

export async function showcasePost(period: "daily" | "weekly", action: "preview" | "send") {
  const r = await authenticatedFetch(`/admin/showcase/${period}/${action}`, { method: "POST", body: "{}" });
  if (!r.ok) throw new Error(`Showcase ${action} failed: ${r.status}`);
  return (await r.json()) as { text: string | null; sent: boolean; reason?: string; channel_configured?: boolean };
}
